import { useMemo, useState } from "react";
import { Repeat } from "lucide-react";
import { MetricToggle } from "./MetricToggle";
import { weeklyOverruns, repeatOffenders } from "../lib/breaks";
import { dayLabel, fmtDur } from "../lib/pickerSheet";
import { slug } from "../lib/format";

const SHOW_WEEKS = 16;
const shortDate = (k) => new Date(`${k}T00:00:00Z`).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit", timeZone: "UTC" });

const Cell = ({ r, minDays, week }) => {
  const cls = !r ? "bg-transparent border border-dashed border-slate-800" : r.over >= minDays ? (r.over >= 3 ? "bg-rose-500" : "bg-rose-500/60") : "bg-slate-700/60";
  const tip = r ? `w/c ${dayLabel(week)}: ${r.over}/${r.days} days over · ${fmtDur(r.overSec)} over` : `w/c ${dayLabel(week)}: did not work`;
  return <span title={tip} className={`inline-block h-4 w-4 rounded-[3px] ${cls}`} />;
};

export const RepeatOffenders = ({ users, cfg, onPicker }) => {
  const [minDays, setMinDays] = useState("1");
  const [minStreak, setMinStreak] = useState("3");
  const { weeks, byWeek } = useMemo(() => weeklyOverruns(users, cfg.breakMin * 60), [users, cfg.breakMin]);
  const rows = useMemo(() => repeatOffenders(weeks, byWeek, +minDays, +minStreak), [weeks, byWeek, minDays, minStreak]);
  const recent = weeks.slice(-SHOW_WEEKS);
  const ongoing = rows.filter((r) => r.current >= +minStreak).length;
  return (
    <div className="panel" data-testid="repeat-offenders">
      <div className="flex flex-wrap items-center gap-3 p-5 border-b border-slate-800">
        <Repeat className="h-5 w-5 text-amber-400" />
        <div className="mr-auto">
          <h3 className="font-display text-lg font-semibold text-slate-100">Repeat lunch overruns</h3>
          <p className="text-xs text-slate-500" data-testid="repeat-summary">{rows.length} pickers overran in {minStreak}+ weeks in a row · {ongoing} still on a run · weeks off don't break a run</p>
        </div>
        <span className="label-mono">Overrun week =</span>
        <MetricToggle value={minDays} onChange={setMinDays} options={[["1", "1+ day"], ["2", "2+ days"], ["3", "3+ days"]]} prefix="repeat-min-days" />
        <span className="label-mono">Run of</span>
        <MetricToggle value={minStreak} onChange={setMinStreak} options={[["3", "3+"], ["4", "4+"], ["6", "6+"]]} prefix="repeat-min-streak" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800">{["Picker", "Current run", "Longest run", "Overrun weeks", `Last ${recent.length} weeks`].map((h) => <th key={h} className="text-left px-3 py-3 font-mono text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap first:pl-5">{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-5 py-6 text-sm text-slate-500">No pickers match — try a lower threshold.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`repeat-row-${slug(r.id)}`} onClick={() => onPicker(r.id)} className="border-b border-slate-800/60 hover:bg-slate-800/40 cursor-pointer transition-colors">
                <td className="pl-5 py-2.5 text-slate-100 font-medium whitespace-nowrap">{r.id}</td>
                <td className="px-3 font-mono whitespace-nowrap" data-testid={`repeat-current-${slug(r.id)}`}>
                  {r.current >= +minStreak ? <span className="px-2 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/40 text-rose-300 text-xs">{r.current} wks · ongoing</span> : <span className="text-slate-500">{r.current || "—"}</span>}
                </td>
                <td className="px-3 font-mono text-slate-200 whitespace-nowrap">{r.best} wks <span className="text-[11px] text-slate-500">{shortDate(r.bestStart)} → {shortDate(r.bestEnd)}</span></td>
                <td className="px-3 font-mono text-slate-300">{r.flagged} / {r.worked} <span className="text-[11px] text-slate-500">({Math.round((r.flagged / r.worked) * 100)}%)</span></td>
                <td className="px-3 py-2"><div className="flex gap-[3px]">{recent.map((w) => <Cell key={w} week={w} r={r.weeks[w]} minDays={+minDays} />)}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-4 px-5 py-3 border-t border-slate-800 text-[11px] text-slate-500">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-[3px] bg-rose-500" /> 3+ overrun days</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-[3px] bg-rose-500/60" /> overrun week</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-[3px] bg-slate-700/60" /> within allowance</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-[3px] border border-dashed border-slate-700" /> not worked</span>
      </div>
    </div>
  );
};
